import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { MessageCircle, Calendar, User, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';
import ConversationDetail from './ConversationDetail';

export default function ConversationsTab({ projectId }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newConv, setNewConv] = useState({ title: '', content: '' });
  const { getAuthHeaders, API } = useAuth();

  useEffect(() => {
    fetchConversations();
  }, [projectId]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${projectId}`, getAuthHeaders());
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddConversation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/conversations`, { ...newConv, project_id: projectId }, getAuthHeaders());
      setNewConv({ title: '', content: '' });
      setAddOpen(false);
      fetchConversations();
    } catch (error) {
      console.error('Failed to add conversation:', error);
    }
  };

  if (selectedConversation) {
    return (
      <ConversationDetail
        conversation={selectedConversation}
        onBack={() => setSelectedConversation(null)}
        projectId={projectId}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Conversations</h2>
          <p className="text-sm text-slate-600 mt-1">Research discussions synced from Claude</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2" data-testid="add-conversation-button">
              <Plus size={18} />
              Add Conversation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading">Add Conversation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddConversation} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <Input
                  value={newConv.title}
                  onChange={(e) => setNewConv({ ...newConv, title: e.target.value })}
                  placeholder="Analysis of Series B SaaS Companies"
                  required
                  data-testid="conversation-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Content</label>
                <Textarea
                  value={newConv.content}
                  onChange={(e) => setNewConv({ ...newConv, content: e.target.value })}
                  placeholder="Paste your Claude conversation here..."
                  rows={12}
                  required
                  data-testid="conversation-content-input"
                />
              </div>
              <Button type="submit" className="btn-primary w-full" data-testid="conversation-submit-button">
                Add Conversation
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="card text-center py-20">
          <MessageCircle className="mx-auto text-slate-300 mb-4" size={64} />
          <h3 className="text-xl font-medium text-slate-900 mb-2">No conversations yet</h3>
          <p className="text-slate-600 mb-6">Add your first conversation from Claude Projects</p>
          <Button onClick={() => setAddOpen(true)} className="btn-primary" data-testid="empty-add-conversation-button">
            <Plus size={18} className="mr-2" />
            Add Conversation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              onClick={() => setSelectedConversation(conv)}
              className="card-interactive p-6"
              data-testid="conversation-item"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">{conv.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {conv.content.substring(0, 200)}...
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {format(new Date(conv.synced_at), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MessageCircle className="text-blue-600" size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
